import { IPrismaClientRequestRPC } from '@prisma/studio-types'

import ModelStore from 'stores/model/ModelStore'
import FieldStore from 'stores/field/FieldStore'
import RecordStore from 'stores/record/RecordStore'

import createError from 'utils/createError'

/**
 * Runs a given query, and creates Records from the response
 *
 * @param code The Prisma Client query to run
 * @param options.throwError Boolean that decides whether an error is thrown or swallowed. Useful if you want to handle the error on your own.
 */
const runQuery = async (
  code: string
): Promise<{ modelId: string; fieldIds: string[]; recordIds: string[] }> => {
  let { error, data } = await window.transport.request<IPrismaClientRequestRPC>(
    {
      channel: 'prisma',
      action: 'clientRequest',
      payload: {
        data: {
          query: code,
        },
      },
    }
  )

  if (error) {
    throw createError(
      'runQuery',
      `Error in Prisma Client request: \n\n${error.stack}`,
      {
        code,
        error,
      }
    )
  }

  if (!data) {
    throw createError(
      'runQuery',
      `Malformed response from Prisma Client: \n\n${data}`,
      {
        code,
      }
    )
  }

  let { params, response } = data

  const model = ModelStore.getByName(params.model)
  if (!model) {
    throw createError('runQuery', 'Unrecognized Model', {
      params,
      response,
    })
  }

  const fieldIds = Object.entries(
    params.args.select || params.args.include || {}
  )
    .filter(([_, exists]) => !!exists)
    .map(([fieldName]) => FieldStore.getByName(model.id, fieldName)?.id)
    .filter(f => !!f)

  if (!response) {
    return Promise.resolve({
      modelId: model.id,
      fieldIds: fieldIds as string[], // It's okay, we're filtering out null values above, TS just doesn't understand it yet
      recordIds: [],
    })
  }

  if (!Array.isArray(response)) {
    response = [response]
  }

  const recordIds = response.map(
    (recordValue: any) =>
      RecordStore.add({
        modelId: model.id,
        value: recordValue,
      }).id
  ) // Cache all records as well

  return {
    modelId: model.id,
    fieldIds: fieldIds as string[], // It's okay, we're filtering out null values above, TS just doesn't understand it yet,
    recordIds,
  }
}

export default runQuery
